import React, { useState, useRef } from "react";
import { XMarkIcon, PaperClipIcon } from "@heroicons/react/24/outline";

interface FileSubmissionProps {
  conversationId: string;
  onSubmit: (files: File[]) => void;
}

const FileSubmission: React.FC<FileSubmissionProps> = ({
  conversationId,
  onSubmit,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (selectedFiles.length > 0) {
      onSubmit(selectedFiles);
      setSelectedFiles([]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-1 p-4 overflow-y-auto">
        {selectedFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <PaperClipIcon className="w-16 h-16 mb-4" />
            <p className="text-center">Aucun fichier sélectionné</p>
            <p className="text-sm text-center mt-2">
              Cliquez sur le bouton ci-dessous pour sélectionner des fichiers
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <PaperClipIcon className="w-6 h-6 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-800">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="p-1 text-gray-500 hover:text-red-500 rounded-full hover:bg-gray-100"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-4">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
          >
            Sélectionner des fichiers
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedFiles.length === 0}
            className={`flex-1 px-4 py-2 text-white rounded-lg ${
              selectedFiles.length === 0
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileSubmission;
